import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { API } from '../App';
import { plainCardText } from '../searchCards';

export default function CardVersionHistory({ box, token, onUpdate, onClose }) {
  const [versions, setVersions] = useState(null);
  const [currentUpdatedAt, setCurrentUpdatedAt] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    let active = true;
    fetch(`${API}/api/boxes/${box.id}/versions`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(response => {
      if (!response.ok) throw new Error(`Could not load history (HTTP ${response.status})`);
      return response.json();
    }).then(history => {
      if (active) {
        setCurrentUpdatedAt(history.updated_at);
        setVersions(history.versions);
        setSelectedId(history.versions[0]?.id || null);
      }
    }).catch(loadError => { if (active) setError(loadError.message); });
    return () => { active = false; };
  }, [box.id, token]);

  const selected = versions?.find(version => version.id === selectedId);

  async function restore() {
    if (!selected || restoring || !window.confirm(`Restore this earlier version of "${box.title || 'Untitled card'}"? The current version will be saved in history.`)) return;
    setRestoring(true);
    setError('');
    try {
      const response = await fetch(`${API}/api/boxes/${box.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: selected.title,
          content_type: selected.content_type,
          content: selected.content,
          images: selected.images,
          expected_updated_at: currentUpdatedAt,
          edit_source: 'restore',
        }),
      });
      if (!response.ok) throw new Error(response.status === 409 ? 'This card changed while history was open. Close and reopen history before restoring.' : `Restore failed (HTTP ${response.status})`);
      onUpdate?.(await response.json());
      onClose();
    } catch (restoreError) {
      setError(restoreError.message);
      setRestoring(false);
    }
  }

  return createPortal(
    <div onMouseDown={event => { if (event.target === event.currentTarget) onClose(); }} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="dialog" aria-modal="true" aria-label={`Version history for ${box.title || 'Untitled card'}`} onKeyDown={event => { if (event.key === 'Escape') onClose(); }} style={{ width: '100%', maxWidth: 760, maxHeight: '85vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-2)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 14, boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ flex: 1, fontWeight: 700, fontSize: 16 }}>Version history · {box.title || 'Untitled card'}</div>
          <button type="button" onClick={onClose} aria-label="Close version history" style={{ border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'auto', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', maxHeight: '55vh', overflowY: 'auto', borderRight: '1px solid var(--border)', padding: 10 }}>
            {versions === null && !error && <div style={{ padding: 12 }}>Loading history…</div>}
            {versions?.length === 0 && <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>No earlier versions yet. The next edit to this card will save one.</div>}
            {versions?.map(version => <button key={version.id} type="button" onClick={() => setSelectedId(version.id)} style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', border: 'none', borderRadius: 8, padding: 10, marginBottom: 4, background: selectedId === version.id ? 'var(--bg-4)' : 'transparent', color: 'var(--text-primary)', font: 'inherit' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{version.title || 'Untitled card'}</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{new Date(Number(version.created_at)).toLocaleString()} · Before {version.source === 'ai' ? 'AI edit' : version.source === 'restore' ? 'restore' : 'edit'}{version.actor_email ? ` by ${version.actor_email}` : ''}</div>
            </button>)}
          </div>
          <div style={{ flex: '2 1 300px', padding: 18, overflowY: 'auto', maxHeight: '55vh' }}>
            {selected && <>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>{selected.title || 'Untitled card'}</div>
              <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: 'var(--text-muted)' }}>{plainCardText(selected.content) || '(Empty card)'}</div>
              {selected.images?.length > 0 && <div style={{ fontSize: 12, marginTop: 12, color: 'var(--text-muted)' }}>{selected.images.length} attached image{selected.images.length === 1 ? '' : 's'}</div>}
            </>}
          </div>
        </div>
        {error && <div role="alert" style={{ padding: '8px 20px', color: '#ef4444', fontSize: 13 }}>{error}</div>}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 14px', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-primary)', borderRadius: 8 }}>Cancel</button>
          <button type="button" disabled={!selected || restoring} onClick={restore} style={{ padding: '8px 14px', cursor: selected && !restoring ? 'pointer' : 'not-allowed', border: 'none', background: 'var(--text-primary)', color: 'var(--bg)', borderRadius: 8, opacity: selected && !restoring ? 1 : 0.5 }}>{restoring ? 'Restoring…' : 'Restore this version'}</button>
        </div>
      </div>
    </div>, document.body
  );
}
